const BOOTSTRAP_CACHE_KEY = "tt_messenger_bootstrap_cache_v1";
const BOOTSTRAP_CACHE_TTL_MS = 60 * 1000;
const BOOTSTRAP_REFRESH_CHECK_INTERVAL_MS = 60 * 1000;
const BOOTSTRAP_REFRESH_WINDOW_MS = 2 * 60 * 1000;
const BOOTSTRAP_REFRESH_INITIAL_BACKOFF_MS = 15 * 1000;
const BOOTSTRAP_REFRESH_MAX_BACKOFF_MS = 5 * 60 * 1000;

const readCachedBootstrap = () => {
  try {
    const raw = window.sessionStorage.getItem(BOOTSTRAP_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.savedAt) {
      return null;
    }

    if (Date.now() - parsed.savedAt > BOOTSTRAP_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(BOOTSTRAP_CACHE_KEY);
      return null;
    }

    return parsed.data;
  } catch (error) {
    return null;
  }
};

const cacheBootstrap = (data) => {
  try {
    window.sessionStorage.setItem(
      BOOTSTRAP_CACHE_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        data,
      })
    );
  } catch (error) {
    // Ignore cache write issues and continue with runtime data.
  }
};

export const getCachedMessengerBootstrap = () => readCachedBootstrap();

export const clearCachedMessengerBootstrap = () => {
  try {
    window.sessionStorage.removeItem(BOOTSTRAP_CACHE_KEY);
  } catch (error) {
    // No-op.
  }
};

export const createMessengerBootstrap = async () => {
  // De-duplicate concurrent bootstrap creation requests.
  // Both `MainLayout` (auto refresh) and `MessengerPanel` can trigger this.
  // Without this, the embedded messenger bootstrap becomes slower than necessary.
  if (createMessengerBootstrap._inFlightPromise) {
    return createMessengerBootstrap._inFlightPromise;
  }

  createMessengerBootstrap._inFlightPromise = (async () => {
    const response = await fetch("/api/messenger/bootstrap", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(data.error || "Не удалось подготовить запуск мессенджера");
    }

    cacheBootstrap(data);
    return data;
  })();

  try {
    return await createMessengerBootstrap._inFlightPromise;
  } finally {
    createMessengerBootstrap._inFlightPromise = null;
  }
};

// Holds the shared in-flight promise for `createMessengerBootstrap()`.
// eslint-disable-next-line no-underscore-dangle
createMessengerBootstrap._inFlightPromise = null;

export const prewarmMessengerBootstrap = () => {
  if (readCachedBootstrap()) {
    return;
  }

  createMessengerBootstrap().catch(() => {
    // Prewarm is best-effort and must not block login flow.
  });
};

const getBootstrapExpiresAtMs = (bootstrap) => {
  if (!bootstrap?.expires_at) {
    return null;
  }

  const timestamp = new Date(bootstrap.expires_at).getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }

  return timestamp;
};

const shouldRefreshBootstrap = (bootstrap) => {
  if (!bootstrap) {
    return true;
  }

  const expiresAtMs = getBootstrapExpiresAtMs(bootstrap);
  if (!expiresAtMs) {
    return true;
  }

  return expiresAtMs - Date.now() <= BOOTSTRAP_REFRESH_WINDOW_MS;
};

export const startMessengerBootstrapAutoRefresh = () => {
  let timerId = null;
  let inFlight = false;
  let stopped = false;
  let backoffMs = 0;

  const schedule = (delayMs) => {
    if (stopped) {
      return;
    }

    window.clearTimeout(timerId);
    timerId = window.setTimeout(() => {
      refreshTick();
    }, delayMs);
  };

  const refreshTick = async () => {
    if (stopped || inFlight) {
      return;
    }

    if (document.hidden) {
      schedule(BOOTSTRAP_REFRESH_CHECK_INTERVAL_MS);
      return;
    }

    const cachedBootstrap = readCachedBootstrap();
    if (!shouldRefreshBootstrap(cachedBootstrap)) {
      schedule(BOOTSTRAP_REFRESH_CHECK_INTERVAL_MS);
      return;
    }

    inFlight = true;
    try {
      await createMessengerBootstrap();
      backoffMs = 0;
      schedule(BOOTSTRAP_REFRESH_CHECK_INTERVAL_MS);
    } catch (error) {
      backoffMs = backoffMs
        ? Math.min(backoffMs * 2, BOOTSTRAP_REFRESH_MAX_BACKOFF_MS)
        : BOOTSTRAP_REFRESH_INITIAL_BACKOFF_MS;
      schedule(backoffMs);
    } finally {
      inFlight = false;
    }
  };

  refreshTick();

  return () => {
    stopped = true;
    window.clearTimeout(timerId);
  };
};
